// src/components/admin/ElectionControl.js
import React, {useState, useEffect} from 'react';
import {electionApi} from '../../api/election';

const candName = (c) => `${c?.first_name ?? c?.member?.first_name ?? ''} ${c?.last_name ?? c?.member?.last_name ?? ''}`.trim();
const candPhoto = (c) => c?.photo_data || c?.photo_url || null;

const ElectionControl = ({auth}) => {
    const [data, setData] = useState(null);          // /admin/election/settings -> {is_open, round_1, round_2, winner...}
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [duration1, setDuration1] = useState(2);
    const [duration2, setDuration2] = useState(2);
    const [results1, setResults1] = useState([]);
    const [results2, setResults2] = useState([]);
    const [activeResults, setActiveResults] = useState(null); // 1 или 2 — какой тур показываем
    const [correction, setCorrection] = useState({candidateId: '', delta: 1, round: 1});
    const [winnerLocal, setWinnerLocal] = useState(null);

    const loadData = async () => {
        setLoading(true);
        setError(null);
        try {
            const settingsRes = await electionApi.getElectionSettings({auth});
            setData(settingsRes.data);
            if (settingsRes.data.winner) setWinnerLocal(settingsRes.data.winner);
            // Подгружаем результаты обоих существующих туров
            if (settingsRes.data.round_1) {
                try {
                    const r = await electionApi.getResults(1, auth);
                    setResults1(r.data);
                } catch (e) { setResults1([]); }
            }
            if (settingsRes.data.round_2) {
                try {
                    const r = await electionApi.getResults(2, auth);
                    setResults2(r.data);
                } catch (e) { setResults2([]); }
            }
        } catch (err) {
            console.error('Ошибка загрузки данных:', err);
            setError('Не удалось загрузить настройки голосования.');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleOpenClose = async () => {
        try {
            await electionApi.updateElectionSettings({is_open: !data.is_open}, auth);
            await loadData();
        } catch (err) {
            alert('Ошибка изменения статуса');
        }
    };

    const handleStartRound = async (roundNumber) => {
        const dur = roundNumber === 1 ? duration1 : duration2;
        try {
            const res = await electionApi.startRound(roundNumber, dur, auth);
            alert(`Тур ${roundNumber} запущен до ${new Date(res.data.round.end_time).toLocaleTimeString()}`);
            await loadData();
        } catch (err) {
            alert(err.response?.data?.detail || 'Ошибка запуска');
        }
    };

    const handleFinishRound = async () => {
        try {
            await electionApi.finishRound(auth);
            alert('Тур завершён');
            await loadData();
        } catch (err) {
            alert(err.response?.data?.detail || 'Ошибка завершения');
        }
    };

    const handlePublishResults = async (roundNumber) => {
        if (!roundNumber) return;
        try {
            await electionApi.publishResults(roundNumber, auth);
            alert(`Результаты ${roundNumber} тура опубликованы`);
            await loadData();
        } catch (err) {
            alert(err.response?.data?.detail || 'Ошибка публикации');
        }
    };

    const handleFinalize = async (candidateId, inFinal) => {
        try {
            await electionApi.finalizeCandidate(candidateId, inFinal, {auth});
            await loadData();
        } catch (err) {
            alert(err.response?.data?.detail || 'Ошибка изменения финалиста');
        }
    };

const handleSelectWinner = async (candidateId) => {
        if (!window.confirm('Выбрать этого кандидата победителем?')) return;
        try {
            await electionApi.selectWinner(candidateId, {auth});
            alert('Победитель выбран');
            await loadData();
        } catch (err) {
            alert(err.response?.data?.detail || 'Ошибка');
        }
    };

    const handleClearWinner = async () => {
        if (!window.confirm('Снять победителя? Победителя нужно будет выбрать заново.')) return;
        try {
            await electionApi.clearWinner({auth});
            alert('Победитель снят');
            await loadData();
        } catch (err) {
            alert(err.response?.data?.detail || 'Ошибка');
        }
    };

    const handleChangeWinner = () => {
        setActiveResults(2);
    };

    const handleCorrect = async () => {
        if (!correction.candidateId || correction.delta === 0) { alert('Выберите кандидата и дельту'); return; }
        const roundNumber = Number(correction.round);
        const roundId = (roundNumber === 2 ? data?.round_2?.id : data?.round_1?.id);
        if (!roundId) { alert('Тур не создан'); return; }
        try {
            await electionApi.correctVotes(roundId, correction.candidateId, correction.delta, auth);
            alert('Скорректировано');
            const r = await electionApi.getResults(roundNumber, auth);
            if (roundNumber === 2) setResults2(r.data); else setResults1(r.data);
            setActiveResults(roundNumber);
        } catch (err) {
            alert(err.response?.data?.detail || 'Ошибка корректировки');
        }
    };

    const handlePreview = () => {
        window.open('/vote?preview=true', '_blank');
    };

    const handleReset = async () => {
        if (!window.confirm('Сбросить голосование? Все туры и голоса будут удалены. Кандидаты и фразы останутся.')) return;
        try {
            await electionApi.resetElection(auth);
            alert('Голосование сброшено');
            await loadData();
        } catch (err) {
            alert(err.response?.data?.detail || 'Ошибка сброса');
        }
    };

    if (loading) return <div className="text-center py-4">Загрузка...</div>;
    if (error) return <div className="text-red-600 py-4">{error}</div>;
    if (!data) return <div className="text-yellow-600 py-4">Настройки не загружены</div>;

    const r1 = data.round_1;
    const r2 = data.round_2;

    const showResultsTable = () => {
        const results = activeResults === 2 ? results2 : results1;
        const roundObj = activeResults === 2 ? r2 : r1;
        const title = activeResults === 2 ? 'Результаты 2 тура' : 'Результаты 1 тура';
        return (
            <table className="w-full border">
                <thead>
                <tr className="bg-gray-100">
                    <th className="p-2 text-left">Кандидат</th>
                    <th className="p-2 text-left">Голосов</th>
                    <th className="p-2 text-left">Действия</th>
                </tr>
                </thead>
                <tbody>
                {results.map((item) => (
                    <tr key={item.candidate.id} className="border-t">
                        <td className="p-2 flex items-center gap-2">
                            {candPhoto(item.candidate) && (
                                <img src={candPhoto(item.candidate)} className="h-8 w-8 rounded object-cover" alt=""/>
                            )}
                            {candName(item.candidate)}
                        </td>
                        <td className="p-2 font-semibold">{item.votes}</td>
                        <td className="p-2">
                            {activeResults === 1 && (
                                <button onClick={() => handleFinalize(item.candidate.id, !item.candidate.in_final)}
                                        className={`text-sm mr-2 ${item.candidate.in_final ? 'text-red-700' : 'text-green-700'}`}>
                                    {item.candidate.in_final ? '✕ убрать из финала' : '← вынести в финал'}
                                </button>
                            )}
                            {activeResults === 2 && (
                                <button onClick={() => handleSelectWinner(item.candidate.id)}
                                        className="text-green-700 text-sm">🏆 Выбрать победителем
                                </button>
                            )}
                        </td>
                    </tr>
                ))}
                {results.length === 0 && (
                    <tr><td className="p-2 text-gray-500" colSpan={3}>Пока нет голосов</td></tr>
                )}
                </tbody>
            </table>
        );
    };

    const roundStatus = (r) => r
        ? ({waiting: '⏳ подготовлен', active: '▶️ идёт', finished: '🔎 завершён', results_published: '📊 опубликован'}[r.status] || r.status)
        : '—';

    return (
        <div className="bg-white p-6 rounded-lg shadow mb-8">
            <h2 className="text-2xl mb-4">Управление голосованием</h2>

            {/* Статусы туров */}
            <div className="grid md:grid-cols-2 gap-4 mb-6">
                <div className="border rounded p-4">
                    <div className="flex justify-between items-center mb-2">
                        <h3 className="text-lg font-semibold">1 тур</h3>
                        <span className="text-sm">{roundStatus(r1)}</span>
                    </div>
                    <div className="flex items-center gap-2 mb-2">
                        <label className="text-sm">Длительность (мин)</label>
                        <input type="number" min={1} value={duration1}
                               onChange={(e) => setDuration1(parseInt(e.target.value) || 2)}
                               className="border rounded p-1 w-20"/>
                    </div>
                </div>
                <div className="border rounded p-4">
                    <div className="flex justify-between items-center mb-2">
                        <h3 className="text-lg font-semibold">2 тур (топ-5)</h3>
                        <span className="text-sm">{roundStatus(r2)}</span>
                    </div>
                    <div className="flex items-center gap-2 mb-2">
                        <label className="text-sm">Длительность (мин)</label>
                        <input type="number" min={1} value={duration2}
                               onChange={(e) => setDuration2(parseInt(e.target.value) || 2)}
                               className="border rounded p-1 w-20"/>
                    </div>
                    {r2 && <p className="text-xs text-gray-500">Участвуют финалисты по итогам 1 тура</p>}
                </div>
            </div>

            {/* Главные кнопки */}
            <div className="flex flex-wrap gap-2 items-center mb-6">
                <button onClick={handleOpenClose}
                        className={`px-4 py-2 rounded text-white ${data.is_open ? 'bg-red-600 hover:bg-red-700' : 'bg-green-600 hover:bg-green-700'}`}>
                    {data.is_open ? 'Закрыть голосование' : 'Открыть голосование'}
                </button>
                <span className="text-sm mr-4">Статус: {data.is_open ? 'Открыто' : 'Закрыто'}</span>

                <button onClick={() => handleStartRound(1)}
                        disabled={r1?.status === 'active' || !data.is_open}
                        className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 disabled:opacity-50">
                    Начать 1 тур
                </button>
                <button onClick={() => handleStartRound(2)}
                        disabled={r2?.status === 'active' || !data.is_open}
                        className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 disabled:opacity-50">
                    Начать 2 тур
                </button>
                <button onClick={handleFinishRound}
                        disabled={!r1 && !r2}
                        className="bg-orange-600 text-white px-4 py-2 rounded hover:bg-orange-700 disabled:opacity-50">
                    Завершить активный тур
                </button>
                <button onClick={() => handlePublishResults(r1?.status === 'finished' ? 1 : (r2?.status === 'finished' ? 2 : null))}
                        disabled={(r1?.status !== 'finished' && r2?.status !== 'finished')}
                        className="bg-purple-600 text-white px-4 py-2 rounded hover:bg-purple-700 disabled:opacity-50">
                    Опубликовать результаты
                </button>
                <button onClick={handlePreview}
                        className="bg-gray-600 text-white px-4 py-2 rounded hover:bg-gray-700">Предпросмотр
                </button>
                <button onClick={handleReset}
                        className="bg-red-800 text-white px-4 py-2 rounded hover:bg-red-900 ml-auto">Сбросить всё
                </button>
            </div>

            {/* Просмотр результатов */}
            <div className="flex gap-2 mb-3">
                <button onClick={() => { setActiveResults(1); }}
                        className={`px-3 py-1 rounded ${activeResults === 1 ? 'bg-gold-accent text-white' : 'bg-gray-200'}`}>
                    Результаты 1 тура
                </button>
                <button onClick={() => { setActiveResults(2); }}
                    className={`px-3 py-1 rounded ${activeResults === 2 ? 'bg-gold-accent text-white' : 'bg-gray-200'}`}>
                    Результаты 2 тура
                </button>
            </div>

            {showResultsTable()}

            {/* Корректировка голосов */}
            <div className="mt-6 p-4 bg-gray-50 rounded flex flex-wrap items-end gap-3">
                <div>
                    <label className="block text-sm">Тур</label>
                    <select value={correction.round} onChange={(e) => setCorrection({...correction, round: Number(e.target.value)})}
                            className="border rounded p-1">
                        <option value="1">1 тур</option>
                        <option value="2">2 тур</option>
                    </select>
                </div>
                <div>
                    <label className="block text-sm">Кандидат</label>
                    <select value={correction.candidateId || ''}
                            onChange={(e) => setCorrection({...correction, candidateId: parseInt(e.target.value)})}
                            className="border rounded p-1">
                        <option value="">— выберите —</option>
                        {(correction.round === 2 ? results2 : results1).map(it => (
                            <option key={it.candidate.id} value={it.candidate.id}>{candName(it.candidate)}</option>
                        ))}
                    </select>
                </div>
                <div>
                    <label className="block text-sm">Дельта (+/−)</label>
                    <input type="number" value={correction.delta}
                           onChange={(e) => setCorrection({...correction, delta: parseInt(e.target.value) || 0})}
                           className="border rounded p-1 w-20"/>
                </div>
                <button onClick={handleCorrect} className="bg-gray-700 text-white px-4 py-2 rounded hover:bg-gray-800">
                    Применить
                </button>
            </div>

            {winnerLocal && (
                <div className="mt-6 p-4 bg-green-50 border border-green-200 rounded flex flex-wrap items-center justify-between gap-3">
                    <div>
                        🏆 Победитель: <b>{candName(winnerLocal)}</b>
                    </div>
                    <div className="flex gap-2">
                        <button onClick={handleChangeWinner}
                                className="px-3 py-1 bg-blue-600 text-white text-sm rounded hover:bg-blue-700">
                            Сменить
                        </button>
                        <button onClick={handleClearWinner}
                                className="px-3 py-1 bg-red-600 text-white text-sm rounded hover:bg-red-700">
                            Убрать победителя
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ElectionControl;